import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Building2, LogOut, ArrowLeft, Users, UserPlus, Check, X, 
  Filter, ChevronDown, Eye, Clock, TrendingUp, Award, Gift,
  Calendar
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

export default function AdminReferralMonitoring() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState(null);
  const [topReferrers, setTopReferrers] = useState([]);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    status: 'all',
    invited_role: 'all',
    date_from: '',
    date_to: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [offset, setOffset] = useState(0);
  const limit = 25;

  useEffect(() => { checkAuth(); }, []);
  useEffect(() => { if (user) fetchReferrals(); }, [filters, offset, user]);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      if (response.data.user_type !== 'admin') { navigate('/login'); return; }
      setUser(response.data);
    } catch { navigate('/login'); }
  };

  const fetchReferrals = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.invited_role !== 'all') params.append('invited_role', filters.invited_role);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      params.append('limit', limit);
      params.append('offset', offset);
      const response = await axios.get(`${BACKEND_URL}/api/admin/referrals?${params.toString()}`, { withCredentials: true });
      setReferrals(response.data.referrals);
      setTotal(response.data.total);
      setStats(response.data.stats);
      setTopReferrers(response.data.top_referrers || []);
    } catch { toast.error('Failed to fetch referrals'); }
    finally { setLoading(false); }
  };

  const handleQualify = async (referralId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/referrals/${referralId}/qualify`, {}, { withCredentials: true });
      toast.success('Referral qualified');
      fetchReferrals();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to qualify referral'); }
  };

  const handleReject = async (referralId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/admin/referrals/${referralId}/reject`, {}, { withCredentials: true });
      toast.success('Referral rejected');
      fetchReferrals();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to reject referral'); }
  };

  const handleLogout = async () => {
    try { await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true }); }
    finally { navigate('/login'); }
  };

  const StatusBadge = ({ status }) => {
    const map = {
      pending:   'bg-yellow-100 text-yellow-700',
      qualified: 'bg-blue-100 text-blue-700',
      rewarded:  'bg-green-100 text-green-700',
      rejected:  'bg-red-100 text-red-700',
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${map[status] || 'bg-slate-100 text-slate-700'}`}
        data-testid={`status-badge-${status}`}>
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  if (loading && !referrals.length) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="loading-spinner">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/admin/dashboard')} className="text-slate-600 hover:text-slate-900">
              <ArrowLeft className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <Building2 className="h-6 w-6 text-orange-500" />
              <h1 className="text-xl font-bold text-slate-900">Referral Monitoring</h1>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-600">{user?.name}</span>
            <button onClick={handleLogout} className="btn-secondary text-sm flex items-center gap-2" data-testid="logout-btn">
              <LogOut className="h-4 w-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8" data-testid="referral-stats-grid">
            {[
              { label: 'Total', value: stats.total, icon: Users, color: 'blue' },
              { label: 'Pending', value: stats.pending, icon: Clock, color: 'yellow' },
              { label: 'Qualified', value: stats.qualified, icon: Check, color: 'sky' },
              { label: 'Rewarded', value: stats.rewarded, icon: Gift, color: 'green' },
              { label: 'Rejected', value: stats.rejected, icon: X, color: 'red' },
            ].map(({ label, value, icon: Icon, color }) => (
              <div className="card" key={label} data-testid={`stat-${label.toLowerCase()}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-3 bg-${color}-100 rounded-lg`}>
                    <Icon className={`h-5 w-5 text-${color}-600`} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{value}</p>
                    <p className="text-sm text-slate-600">{label}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Top Referrers */}
        {topReferrers.length > 0 && (
          <div className="card mb-6" data-testid="top-referrers-section">
            <h3 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-orange-500" /> Top Referrers
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              {topReferrers.map((tr, i) => (
                <div key={tr.user_id} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                  <span className="text-lg font-bold text-orange-500">#{i + 1}</span>
                  <div className="min-w-0">
                    <p className="font-medium text-slate-900 truncate">{tr.name}</p>
                    <p className="text-xs text-slate-500 truncate">{tr.email}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${tr.user_type === 'tradesperson' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                        {tr.user_type}
                      </span>
                      <span className="text-xs font-bold text-green-600">{tr.successful_referrals} qualified</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900">Filters</h3>
            <button onClick={() => setShowFilters(!showFilters)} className="btn-secondary text-sm flex items-center gap-2">
              <Filter className="h-4 w-4" />
              {showFilters ? 'Hide' : 'Show'} Filters
              <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
            </button>
          </div>
          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-slate-200">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Status</label>
                <select value={filters.status} onChange={(e) => { setOffset(0); setFilters({...filters, status: e.target.value}); }} className="w-full" data-testid="filter-status">
                  <option value="all">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="qualified">Qualified</option>
                  <option value="rewarded">Rewarded</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">User Type</label>
                <select value={filters.invited_role} onChange={(e) => { setOffset(0); setFilters({...filters, invited_role: e.target.value}); }} className="w-full" data-testid="filter-role">
                  <option value="all">All Types</option>
                  <option value="tradesperson">Tradespeople</option>
                  <option value="employer">Employers</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">From Date</label>
                <input type="date" value={filters.date_from} onChange={(e) => { setOffset(0); setFilters({...filters, date_from: e.target.value}); }} className="w-full" data-testid="filter-date-from" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">To Date</label>
                <input type="date" value={filters.date_to} onChange={(e) => { setOffset(0); setFilters({...filters, date_to: e.target.value}); }} className="w-full" data-testid="filter-date-to" />
              </div>
            </div>
          )}
        </div>

        {/* Referrals Table */}
        <div className="card">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Referrals ({total})</h3>
          {referrals.length === 0 ? (
            <div className="text-center py-12">
              <UserPlus className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600 text-lg">No referrals found</p>
              <p className="text-slate-500 text-sm">Referrals will appear here when users sign up with referral codes</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="referrals-table">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-3 text-xs font-medium text-slate-600">Referrer</th>
                    <th className="text-left py-3 px-3 text-xs font-medium text-slate-600">Referred User</th>
                    <th className="text-left py-3 px-3 text-xs font-medium text-slate-600">Type</th>
                    <th className="text-left py-3 px-3 text-xs font-medium text-slate-600">Code</th>
                    <th className="text-left py-3 px-3 text-xs font-medium text-slate-600">Status</th>
                    <th className="text-left py-3 px-3 text-xs font-medium text-slate-600">Reward</th>
                    <th className="text-left py-3 px-3 text-xs font-medium text-slate-600">Created</th>
                    <th className="text-left py-3 px-3 text-xs font-medium text-slate-600">Qualified</th>
                    <th className="text-left py-3 px-3 text-xs font-medium text-slate-600">Rewarded</th>
                    <th className="text-left py-3 px-3 text-xs font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((ref) => (
                    <tr key={ref.referral_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`referral-row-${ref.referral_id}`}>
                      <td className="py-3 px-3">
                        <p className="font-medium text-sm text-slate-900">{ref.referrer?.name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500">{ref.referrer?.email}</p>
                      </td>
                      <td className="py-3 px-3">
                        <p className="font-medium text-sm text-slate-900">{ref.invited?.name || 'Unknown'}</p>
                        <p className="text-xs text-slate-500">{ref.invited?.email}</p>
                        {ref.invited?.approval_status && (
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                            ref.invited.approval_status === 'approved' ? 'bg-green-100 text-green-700' :
                            ref.invited.approval_status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                          }`}>
                            {ref.invited.approval_status}
                          </span>
                        )}
                      </td>
                      <td className="py-3 px-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${ref.invited_role === 'tradesperson' ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}`}>
                          {ref.invited_role}
                        </span>
                      </td>
                      <td className="py-3 px-3">
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded">{ref.referral_code_used}</code>
                      </td>
                      <td className="py-3 px-3"><StatusBadge status={ref.status} /></td>
                      <td className="py-3 px-3">
                        {ref.reward_type ? (
                          <div>
                            <span className="text-xs font-medium text-slate-900">{
                              ref.reward_type === 'on_call_free_month' || ref.reward_type === 'on_call_referral' || ref.reward_type === 'priority_boost'
                                ? 'Priority Boost'
                                : ref.reward_type === 'employer_discount'
                                  ? 'Discount'
                                  : ref.reward_type
                            }</span>
                            {ref.reward_value && <span className="text-xs text-slate-500 ml-1">({ref.reward_value})</span>}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">-</span>
                        )}
                      </td>
                      <td className="py-3 px-3 text-xs text-slate-600">{fmtDate(ref.created_at)}</td>
                      <td className="py-3 px-3 text-xs text-slate-600">{fmtDate(ref.qualified_at)}</td>
                      <td className="py-3 px-3 text-xs text-slate-600">{fmtDate(ref.rewarded_at)}</td>
                      <td className="py-3 px-3">
                        <div className="flex items-center gap-1">
                          {ref.status === 'pending' && (
                            <>
                              <button onClick={() => handleQualify(ref.referral_id)} className="text-green-600 hover:text-green-700 p-1" title="Qualify" data-testid={`qualify-btn-${ref.referral_id}`}>
                                <Check className="h-4 w-4" />
                              </button>
                              <button onClick={() => handleReject(ref.referral_id)} className="text-red-600 hover:text-red-700 p-1" title="Reject" data-testid={`reject-btn-${ref.referral_id}`}>
                                <X className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          <button onClick={() => navigate(`/admin/user/${ref.invited_user_id}`)} className="text-slate-600 hover:text-slate-700 p-1" title="View User">
                            <Eye className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-200">
              <p className="text-sm text-slate-600">
                Showing {offset + 1} - {Math.min(offset + limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <button onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0} className="btn-secondary text-sm disabled:opacity-50">Previous</button>
                <button onClick={() => setOffset(offset + limit)} disabled={offset + limit >= total} className="btn-secondary text-sm disabled:opacity-50">Next</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
